import { FC } from 'react';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { PublicServiceModel } from '@/lib/models/public-service/public-service.model';
import Chip from '@/components/chip';
import { Box, Stack } from '@mui/material';
import { DateFormat, getNiceDate } from '@/lib/helpers';
import StatusChip from '@/components/chip/status-chip';
import { StaticDashboard } from '@/components/metabase/StaticDashboard';
import Environment from '@/lib/environment';
import RiskChip from '@/components/chip/risk-chip';
import NoneChip from '@/components/chip/none-chip';

interface Props {
  publicService: PublicServiceModel;
}

const getReadonlyFields = (data: PublicServiceModel | null): ReadonlyField[] => {
  const deprecationFields: ReadonlyField[] = [];

  if (data?.support.deprecated?.value) {
    const { eolTimestamp, description } = data.support.deprecated;
    if (description) {
      deprecationFields.push({ label: 'Deprecation Notice', prop: 'deprecationNotice' });
    }
    if (eolTimestamp) {
      deprecationFields.push({ label: 'End of Life Date', prop: 'eolTimestamp' });
    }
  }

  return [
    ...deprecationFields,
    ...[
      { label: '', prop: 'analytics' },
      { label: 'Status', prop: 'status', rowGroup: 'status' },
      { label: 'Risk Level', prop: 'risk', rowGroup: 'status' },
      { label: 'Intercept Support', prop: 'support' },
      { label: 'Request Classification', prop: 'tags' },
    ],
  ];
};

const getReadonlyData = (data: PublicServiceModel | null, dashboardId: string | null) => {
  if (!data) {
    return {};
  }

  const {
    name,
    status,
    interceptFeatures,
    promptGuards,
    support,
    isDeprecated,
    eolTimestamp,
    riskLevel,
  } = data;

  const hasDashboardId = !!dashboardId;
  const hasPromptGuards = !!promptGuards.length;
  const hasInterceptFeatures = !!interceptFeatures.length;

  return {
    analytics: hasDashboardId ? (
      <StaticDashboard
        dashboardId={dashboardId}
        initialParameters={{ service_name: name }}
        hiddenParameters={['service_name']}
        isDrawer
      />
    ) : null,
    deprecationNotice: isDeprecated ? support.deprecated?.description : null,
    eolTimestamp: eolTimestamp ? (
      <Box>
        {getNiceDate(eolTimestamp, DateFormat.FRIEND).formatted}
        <Box mt={2}>
          F5 Workforce AI Security will stop accepting traffic for this service after the End of
          Life date.
        </Box>
      </Box>
    ) : null,
    status: <StatusChip value={status} size="small" />,
    support: (
      <Stack gap={0.5} flexWrap="wrap" direction="row">
        {hasInterceptFeatures ? (
          interceptFeatures.map(({ value, label }) => (
            <Chip key={value} label={label as string} size="small" />
          ))
        ) : (
          <NoneChip />
        )}
      </Stack>
    ),
    tags: (
      <Stack gap={0.5} flexWrap="wrap" direction="row">
        {hasPromptGuards ? (
          promptGuards.map(({ value, label }) => (
            <Chip key={value} label={label as string} size="small" />
          ))
        ) : (
          <NoneChip />
        )}
      </Stack>
    ),
    risk: <RiskChip value={riskLevel} />,
  };
};

export const ServiceDetails: FC<Props> = ({ publicService }) => {
  const { METABASE_PS_ENTITY_ID } = Environment;
  const dashboardId = METABASE_PS_ENTITY_ID;

  return (
    <ReadonlyView
      fields={getReadonlyFields(publicService)}
      data={getReadonlyData(publicService, dashboardId)}
    />
  );
};

export default ServiceDetails;
